import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  apiStatusToUi,
  getConcept,
  getConceptsByProject,
  palettesFromApi,
} from "../../../../../services/conceptsService";

const STATUS_CFG = {
  draft: { label: "Draft", bg: "#F3F4F6", color: "#374151" },
  sent_to_user: { label: "Sent to Customer", bg: "#DBEAFE", color: "#1E40AF" },
  approved: { label: "Approved", bg: "#D1FAE5", color: "#065F46" },
};

const StatusPill = ({ status }) => {
  const cfg = STATUS_CFG[status] || STATUS_CFG.draft;
  return (
    <Chip
      label={cfg.label}
      size="small"
      sx={{
        backgroundColor: cfg.bg,
        color: cfg.color,
        fontWeight: 600,
        fontSize: 12,
      }}
    />
  );
};

const PalettePreview = ({ palette }) => {
  if (!palette?.length) return null;
  return (
    <Box sx={{ display: "flex", gap: 0.5 }}>
      {palette.map((hex, i) => (
        <Box
          key={i}
          sx={{
            width: 18,
            height: 18,
            borderRadius: "50%",
            backgroundColor: hex,
            border: "1px solid rgba(0,0,0,0.1)",
          }}
        />
      ))}
    </Box>
  );
};

const ConceptCard = ({ concept, onSelect }) => {
  const status = apiStatusToUi(concept.status);
  const palette = palettesFromApi(concept.palettes);

  return (
    <Box
      onClick={() => onSelect(concept.id)}
      sx={{
        cursor: "pointer",
        border: "1px solid #E5E7EB",
        borderRadius: "12px",
        backgroundColor: "#fff",
        overflow: "hidden",
        boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
        transition: "transform 0.15s, box-shadow 0.15s",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: "0 4px 8px rgba(0,0,0,0.08)",
        },
      }}
    >
      <Box
        sx={{
          aspectRatio: "4 / 3",
          backgroundColor: "#F3F4F6",
          backgroundImage: concept.image_url
            ? `url(${concept.image_url})`
            : undefined,
          backgroundSize: "cover",
          backgroundPosition: "center",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {!concept.image_url && (
          <Typography sx={{ color: "#9CA3AF", fontSize: 13 }}>
            No cover
          </Typography>
        )}
      </Box>
      <Box sx={{ p: 2 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            mb: 0.5,
          }}
        >
          <Typography
            sx={{
              fontSize: 15,
              fontWeight: 600,
              color: "#0D0D0E",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              flex: 1,
            }}
          >
            {concept.name || "Untitled concept"}
          </Typography>
          <StatusPill status={status} />
        </Box>
        {concept.description && (
          <Typography
            sx={{
              fontSize: 13,
              color: "#6B7280",
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              mb: 1,
            }}
          >
            {concept.description.replace(/<[^>]*>/g, "")}
          </Typography>
        )}
        {palette.length > 0 && <PalettePreview palette={palette} />}
      </Box>
    </Box>
  );
};

// ---------- Detail ----------

const SECTION_TYPE_LABELS = {
  floor_plan: "Floor Plans",
  inspiration: "Inspiration",
  furniture: "Furniture",
  material: "Materials",
};

const SectionGrid = ({ section, onImageClick }) => {
  const images = section.images || [];
  return (
    <Box
      sx={{
        border: "1px solid #E5E7EB",
        borderRadius: "12px",
        backgroundColor: "#fff",
        p: 3,
        mb: 3,
      }}
    >
      <Typography
        sx={{
          fontSize: 16,
          fontWeight: 500,
          color: "#0D0D0E",
          mb: 1.5,
        }}
      >
        {SECTION_TYPE_LABELS[section.type] || section.name}
        <Box
          component="span"
          sx={{ color: "#6B7280", fontWeight: 400, ml: 0.5 }}
        >
          ({images.length})
        </Box>
      </Typography>

      {images.length === 0 ? (
        <Typography sx={{ fontSize: 13, color: "#9CA3AF" }}>
          No images.
        </Typography>
      ) : (
        <Box
          sx={{
            display: "grid",
            gap: 1.5,
            gridTemplateColumns: {
              xs: "repeat(2, 1fr)",
              sm: "repeat(3, 1fr)",
              lg: "repeat(4, 1fr)",
            },
          }}
        >
          {images.map((img) => (
            <Box
              key={img.id}
              onClick={() => onImageClick?.(img)}
              sx={{
                aspectRatio: "4 / 3",
                borderRadius: 1.5,
                overflow: "hidden",
                border: "1px solid #F3F4F6",
                cursor: onImageClick ? "pointer" : "default",
              }}
            >
              <Box
                component="img"
                src={img.image_thumbnail || img.url || img.path}
                alt=""
                sx={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

const ConceptDetail = ({ conceptId, onBack }) => {
  const [concept, setConcept] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!conceptId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await getConcept(conceptId);
        if (cancelled) return;
        setConcept(res?.success ? res.data : null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [conceptId]);

  const sections = concept?.sections || [];
  const standardSections = sections.filter(
    (s) => s.type !== "custom" && SECTION_TYPE_LABELS[s.type]
  );
  const customSections = sections.filter((s) => s.type === "custom");
  const palette = useMemo(
    () => palettesFromApi(concept?.palettes),
    [concept?.palettes]
  );

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          py: 6,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!concept) {
    return (
      <Box>
        <IconButton onClick={onBack}>
          <ArrowBackIcon />
        </IconButton>
        <Typography sx={{ color: "#6B7280", fontSize: 14, mt: 2 }}>
          Concept not found.
        </Typography>
      </Box>
    );
  }

  const status = apiStatusToUi(concept.status);

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mb: 3,
          flexWrap: "wrap",
        }}
      >
        <IconButton onClick={onBack} size="small">
          <ArrowBackIcon />
        </IconButton>
        <Typography sx={{ fontSize: 20, fontWeight: 500, color: "#0D0D0E" }}>
          {concept.name || "Untitled concept"}
        </Typography>
        <StatusPill status={status} />
        {concept.latest_sent_version > 0 && (
          <Chip
            label={`v${concept.latest_sent_version}`}
            size="small"
            sx={{
              backgroundColor: "#F3F4F6",
              color: "#374151",
              fontWeight: 600,
              fontSize: 11,
            }}
          />
        )}
      </Box>

      <Box
        sx={{
          display: "grid",
          gap: 3,
          gridTemplateColumns: { xs: "1fr", md: "2fr 3fr" },
          mb: 0,
        }}
      >
        <Box
          sx={{
            border: "1px solid #E5E7EB",
            borderRadius: "12px",
            backgroundColor: "#F3F4F6",
            backgroundImage: concept.image_url
              ? `url(${concept.image_url})`
              : undefined,
            backgroundSize: "cover",
            backgroundPosition: "center",
            aspectRatio: "4 / 3",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {!concept.image_url && (
            <Typography sx={{ color: "#9CA3AF", fontSize: 14 }}>
              No cover image
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            border: "1px solid #E5E7EB",
            borderRadius: "12px",
            backgroundColor: "#fff",
            p: 3,
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Box>
            <Typography
              sx={{ fontSize: 12, color: "#9CA3AF", mb: 0.5, fontWeight: 500 }}
            >
              DESCRIPTION
            </Typography>
            <Typography
              sx={{
                fontSize: 14,
                color: concept.description ? "#0D0D0E" : "#9CA3AF",
                whiteSpace: "pre-wrap",
              }}
            >
              {concept.description
                ? concept.description.replace(/<[^>]*>/g, "")
                : "No description."}
            </Typography>
          </Box>
          <Box>
            <Typography
              sx={{ fontSize: 12, color: "#9CA3AF", mb: 1, fontWeight: 500 }}
            >
              COLOR PALETTE
            </Typography>
            {palette.length > 0 ? (
              <Box sx={{ display: "flex", gap: 2 }}>
                {palette.map((hex, i) => (
                  <Box
                    key={i}
                    sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
                  >
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: "50%",
                        backgroundColor: hex,
                        border: "1px solid rgba(0,0,0,0.1)",
                      }}
                    />
                    <Typography
                      sx={{ fontSize: 11, color: "#6B7280", textAlign: "center" }}
                    >
                      {hex}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography sx={{ fontSize: 13, color: "#9CA3AF" }}>
                No palette set.
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      <Box sx={{ mt: 3 }}>
        {standardSections.map((section) => (
          <SectionGrid key={section.id} section={section} />
        ))}
        {customSections.map((section) => (
          <SectionGrid key={section.id} section={section} />
        ))}
      </Box>
    </Box>
  );
};

// ---------- Top-level switch ----------

const Concepts = ({ projectId }) => {
  const [concepts, setConcepts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await getConceptsByProject(projectId);
        if (cancelled) return;
        setConcepts(
          res?.success && Array.isArray(res.data) ? res.data : []
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  if (selectedId) {
    return (
      <ConceptDetail
        conceptId={selectedId}
        onBack={() => setSelectedId(null)}
      />
    );
  }

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          py: 6,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (concepts.length === 0) {
    return (
      <Box
        sx={{
          textAlign: "center",
          py: 8,
          border: "1px dashed #D1D5DB",
          borderRadius: 2,
        }}
      >
        <Typography sx={{ fontSize: 16, fontWeight: 500, color: "#0D0D0E" }}>
          No concepts yet
        </Typography>
        <Typography sx={{ fontSize: 13, color: "#6B7280", mt: 0.5 }}>
          Concepts created by the design team will appear here.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          mb: 3,
        }}
      >
        <Typography sx={{ fontSize: 20, fontWeight: 500, color: "#0D0D0E" }}>
          Concepts
        </Typography>
        <Chip
          label={`${concepts.length} / 3`}
          size="small"
          sx={{
            backgroundColor: "#F3F4F6",
            color: "#374151",
            fontWeight: 600,
            fontSize: 11,
          }}
        />
      </Box>
      <Box
        sx={{
          display: "grid",
          gap: 2,
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            lg: "repeat(3, 1fr)",
          },
        }}
      >
        {concepts.map((c) => (
          <ConceptCard
            key={c.id}
            concept={c}
            onSelect={(id) => setSelectedId(id)}
          />
        ))}
      </Box>
    </Box>
  );
};

export default Concepts;
